/**
 * Iter-189 Sprint 4 — Homepage Control Center hydration hook.
 *
 * Fetches `/api/homepage` once on mount + exposes the MERGED config
 * (defaults + admin overrides). Hero.jsx + SeasonalBanner consume
 * this hook so admin edits propagate to the public homepage without
 * a code redeploy.
 *
 * First-paint policy: returns an empty merged config (all `null`)
 * while the API request is in flight. Hero.jsx defaults to the
 * hardcoded JSX literals when the corresponding hook value is null,
 * which keeps the 2,453-test regex suite green AND keeps SSR fast.
 *
 * sessionStorage-cached for the lifetime of the tab so navigation
 * back to the homepage doesn't re-hit the API.
 */
import { useEffect, useState } from "react";
import { fetchHomepage } from "./api";

const CACHE_KEY = "carsavr.homepage_data";
const CACHE_TTL_MS = 5 * 60 * 1000;

const EMPTY_CONFIG = {
  hero: {
    headline_lead: null,
    headline_subject: null,
    headline_emphasis: null,
    subhead_mobile: null,
    subhead_desktop: null,
    cta_primary_text: null,
    cta_primary_url: null,
  },
  seasonal_banner: {
    enabled: false,
    text: "",
    cta_text: "",
    cta_url: "",
    tone: "promo",
    starts_at: null,
    ends_at: null,
    active: false,
  },
};

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* sessionStorage full or disabled — swallow */
  }
}

let inflight = null;

async function loadHomepage() {
  const cached = readCache();
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = fetchHomepage()
    .then((d) => {
      writeCache(d);
      inflight = null;
      return d;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function useHomepageData() {
  const [data, setData] = useState(() => readCache() || EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadHomepage()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        /* Already initialized to EMPTY_CONFIG — swallow */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...data, loading };
}

/**
 * Convenience selector for the hero override values. Returns
 * `{ headline_lead, headline_subject, headline_emphasis, ... }` —
 * any field that's not overridden is `null` so the component can
 * fall back cleanly to its JSX literal.
 */
export function useHeroOverrides() {
  const { hero } = useHomepageData();
  return hero || EMPTY_CONFIG.hero;
}

export function useSeasonalBanner() {
  const { seasonal_banner } = useHomepageData();
  return seasonal_banner || EMPTY_CONFIG.seasonal_banner;
}
